<template>
  <page-loader>
    <filter-sidebar
      v-model="filterDrawerOpen"
      :loading="isLoading"
      :show-clear="$_queryHandler_hasQueryChanged"
      clear-text="Reset"
      @clear="resetFilters"
    >
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-subheader>Details</v-subheader>
          </v-col>
          <v-col cols="6">
            <select-list
              v-model="query.params.dealType"
              :disabled="isLoading"
              :items="getItemsForOption('dealTypes')"
              label="Deal Type"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="6">
            <select-list
              v-model="query.params.offerType"
              :disabled="isLoading"
              :items="getItemsForOption('offerTypes')"
              clearable
              label="Supported Offer Type"
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <select-list
              v-model="query.params.countries"
              :disabled="isLoading"
              :items="getItemsForOption('countries')"
              label="Countries"
              hide-details
              clearable
              multiple
            />
          </v-col>
          <v-col cols="12">
            <select-list
              v-model="query.params.partners"
              :disabled="isLoading"
              :items="partnerList"
              :limit="1"
              label="Partner(s)"
              clearable
              hide-details
              multiple
            />
          </v-col>
          <v-col cols="12">
            <select-list
              v-model="query.params.trafficRules"
              :disabled="isLoading"
              :items="trafficRulesOptions"
              label="Traffic Rules"
              clearable
              hide-details
              multiple
            />
          </v-col>
          <v-col cols="12">
            <v-divider />
            <v-subheader>Reduce</v-subheader>
            <toggle-input
              v-model="query.params.hideNoDeal"
              :is-disabled="isLoading"
              label="Only Countries With Deals"
            />
          </v-col>
        </v-row>
      </v-container>
    </filter-sidebar>

    <v-container fluid>
      <v-row>
        <v-col cols="4" class="d-flex align-center">
          <v-tooltip bottom left>
            <template #activator="{ on }">
              <span class="rounded-sm pa-2 mr-2 text-body-2 blue lighten-4" v-on="on">Receive Traffic</span>
            </template>
            <span>Direct a percentage of traffic to this Partner</span>
          </v-tooltip>
          <v-tooltip bottom left>
            <template #activator="{ on }">
              <span class="rounded-sm pa-2 mr-2 text-body-2 orange lighten-4" v-on="on">Limit Traffic</span>
            </template>
            <span
              >Deprioritise traffic to this Partner. Traffic will still be sent here if all other Partners fail to
              register</span
            >
          </v-tooltip>
          <v-tooltip bottom left>
            <template #activator="{ on }">
              <span class="rounded-sm pa-2 mr-2 text-body-2 red lighten-4" v-on="on">Block Traffic</span>
            </template>
            <span>Prevent traffic going to this partner</span>
          </v-tooltip>
        </v-col>
        <v-col cols="8" class="d-flex align-center">
          <filter-chips :data="filterChipsData" class="flex-grow-1" @clear-chip="$_filterChips_clearChip">
            <v-badge color="accent" bordered overlap :content="$_queryHandler_numberOfQueries">
              <v-btn color="accent" small depressed title="Filters" @click="filterDrawerOpen = true">
                <v-icon small left>filter_alt</v-icon>
                <span>Filters</span>
              </v-btn>
            </v-badge>
          </filter-chips>
        </v-col>
      </v-row>
    </v-container>
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card v-show="hasDeals" outlined>
            <div class="table">
              <div class="table__inner">
                <div ref="table-head" class="table__head">
                  <v-tooltip
                    v-for="(header, idx) in headers"
                    :key="`${header.value}-${idx}`"
                    tag="div"
                    max-width="300"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <div
                        class="table__cell table__cell--head"
                        :class="{ 'table__cell--row-head': idx === 0 }"
                        v-on="header.name ? on : null"
                      >
                        <div class="table__cell__text">{{ header.text }}</div>
                      </div>
                    </template>
                    <template>
                      <div>{{ header.name }}</div>
                      <template v-if="header.offerTypes && header.offerTypes.length">
                        <div>Supported Offer Types:</div>
                        <span v-for="(offerType, offerIndex) in header.offerTypes" :key="`${header.name}-${offerType}`">
                          {{ offerType }}{{ offerIndex !== header.offerTypes.length - 1 ? ', ' : null }}
                        </span>
                      </template>
                    </template>
                  </v-tooltip>
                </div>
                <v-virtual-scroll
                  ref="table-body"
                  :items="mappedCountriesToDeals"
                  :item-height="32"
                  max-height="500"
                  bench="16"
                >
                  <template v-slot:default="{ item: row }">
                    <div :key="row.country" class="table__row">
                      <div class="table__cell table__cell--row-head" :title="row.country">
                        <span class="table__cell__text">{{ row.country }}</span>
                      </div>

                      <template v-for="(deal, idx) in row.deals">
                        <v-tooltip
                          v-if="!!deal.grc || !!deal.rate || !!deal.rules"
                          :key="
                            `${row.country}-${query.params.dealType}-${deal.partner}-${query.params.offerType}-tooltip-${idx}`
                          "
                          max-width="300"
                          top
                        >
                          <template v-slot:activator="{ on }">
                            <div
                              :class="{
                                'blue lighten-4': deal.rules && deal.rules.type === 'Receive',
                                'orange lighten-4': deal.rules && deal.rules.type === 'Limit',
                                'red lighten-4': deal.rules && deal.rules.type === 'Block',
                              }"
                              class="table__cell"
                              v-on="on"
                            >
                              <v-icon
                                v-if="deal.rate && deal.rate.qualifier"
                                class="has-qualifier"
                                color="error"
                                x-small
                                >error</v-icon
                              >
                              <span class="table__cell__text">{{ formattedRate(deal) }}</span>
                            </div>
                          </template>
                          <div
                            v-for="detail in formattedDetails(deal)"
                            :key="`${row.country}-${deal.partner}-${detail}-${query.params.offerType}`"
                          >
                            {{ detail }}
                          </div>
                        </v-tooltip>
                        <div
                          v-else
                          :key="
                            `${row.country}-${query.params.dealType}-${deal.partner}-${query.params.offerType}-no-tooltip-${idx}`
                          "
                          :class="{
                            'blue lighten-4': deal.rules && deal.rules.type === 'Receive',
                            'orange lighten-4': deal.rules && deal.rules.type === 'Limit',
                            'red lighten-4': deal.rules && deal.rules.type === 'Block',
                          }"
                          class="table__cell"
                        >
                          <v-icon v-if="deal.rate && deal.rate.qualifier" class="has-qualifier" color="error" x-small
                            >error</v-icon
                          >
                          <span class="table__cell__text">{{ formattedRate(deal) }}</span>
                        </div>
                      </template>
                    </div>
                  </template>
                </v-virtual-scroll>
              </div>
            </div>
          </v-card>
          <empty-list v-show="!hasDeals" type="deal" />
        </v-col>
      </v-row>
      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="auto" class="d-flex align-center">
          <select-list
            v-model="exportOption"
            :items="exportOptions"
            :disabled="isLoading"
            label="Export Option"
            outlined
            hide-details
            class="mr-4"
          />
          <v-btn :disabled="isLoading" :loading="isLoading" color="primary" depressed @click="onClickExport">
            Export as CSV
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </page-loader>
</template>

<script>
// Vue
import { mapGetters, mapMutations } from 'vuex'
// Components
import EmptyList from '@/components/empty-list'
import FilterChips from '@/components/filter-chips'
import FilterSidebar from '@/components/filter-sidebar'
import PageLoader from '@/components/page-loader'
import SelectList from '@/components/forms/select-list'
import ToggleInput from '@/components/forms/toggle-input'
// Mixins
import FilterChipsHandler from '@/mixins/filter-chips'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Services
import { deals, partners, trafficRules } from '@/services/offers'
// Helpers
import { exportCSV } from '@/utils/csv'

const resetQuery = () => ({
  countries: [],
  dealType: 'CPA',
  offerType: 'crypto',
  hideNoDeal: true,
  trafficRules: ['No Rule', 'Receive', 'Limit', 'Block'],
  partners: [],
})

export default {
  name: 'DealsMatrix',
  components: {
    EmptyList,
    FilterChips,
    FilterSidebar,
    SelectList,
    PageLoader,
    ToggleInput,
  },
  mixins: [FilterChipsHandler, PermissionsHandler, QueryParamsHandler],
  data: () => ({
    filterDrawerOpen: false,
    isLoading: false,
    deals: [],
    defaultQuery: resetQuery(),
    exportOption: 'overview',
    exportOptions: [
      { key: 'Overview', value: 'overview' },
      { key: 'GRC', value: 'grc' },
      { key: 'Payout', value: 'payout' },
    ],
    partners: [],
    partnerList: [],
    trafficRules: [],
    trafficRulesOptions: [
      { key: 'No Rule', value: 'No Rule' },
      { key: 'Receive', value: 'Receive' },
      { key: 'Limit', value: 'Limit' },
      { key: 'Block', value: 'Block' },
    ],
    query: {
      params: resetQuery(),
    },
    queriesDefined: ['countries', 'dealType', 'hideNoDeal', 'offerType', 'partners', 'trafficRules'],
    queryDefaults: ['dealType', 'offerType', 'hideNoDeal', 'trafficRules'],
    queryTypes: { trafficRules: 'array', countries: 'array', partners: 'array', hideNoDeal: 'boolean' },
    scrollPosition: 0,
    tableLoaded: false,
    tableRenderCheckInterval: null,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['formattedOption', 'getItemsForOption']),
    countries() {
      const countries = this.getItemsForOption('countries')
      if (!this.query.params.countries || !this.query.params.countries.length) return countries
      return countries.filter(({ value }) => this.query.params.countries.includes(value))
    },
    filterChipsData() {
      return [
        {
          label: 'Deal Type',
          model: 'query.params.dealType',
          options: this.getItemsForOption('dealTypes'),
          value: this.query.params.dealType,
        },
        {
          label: 'Supported Offers',
          model: 'query.params.offerType',
          options: this.getItemsForOption('offerType'),
          value: this.query.params.offerType,
        },
        {
          label: 'Countries',
          model: 'query.params.countries',
          options: this.getItemsForOption('countries'),
          value: this.query.params.countries,
        },
        {
          label: 'Partners',
          model: 'query.params.partners',
          options: this.getItemsForOption('partners'),
          value: this.query.params.partners,
        },
        {
          label: 'Traffic Rules',
          model: 'query.params.trafficRules',
          options: this.trafficRulesOptions,
          value: this.query.params.trafficRules,
        },
        {
          label: 'Only countries with deals',
          model: 'query.params.hideNoDeal',
          value: this.query.params.hideNoDeal,
        },
      ]
    },
    filteredDeals() {
      const { dealType, offerType, partners } = this.query.params
      return this.deals
        .filter(
          ({ offer_types, type, partner }) =>
            ((offer_types && offer_types.includes(offerType)) || !offerType) &&
            (!dealType || type === dealType) &&
            (!partners || !partners.length || partners.includes(partner))
        )
        .sort((a, b) => a.partner.localeCompare(b.partner))
    },
    mappedCountriesToDeals() {
      return this.countries
        .map(({ key, value }) => {
          const dealsForCountry = this.filteredDeals.map(
            ({ commission_rates, conversion_rate_guarantee, currency, partner, notes, type }) => {
              const bestRule =
                this.trafficRules?.[value]?.[partner] && this.getBestTrafficRule(this.trafficRules[value][partner])

              if (this.showDeal(bestRule)) {
                const rate = commission_rates[value] || null
                const grc = conversion_rate_guarantee[value] || null
                return {
                  notes,
                  currency: this.getCurrencySymbol(currency),
                  partner,
                  rate,
                  grc,
                  type,
                  rules: bestRule,
                  empty: !grc && !rate,
                }
              } else {
                return { empty: true }
              }
            }
          )
          if (this.query.params.hideNoDeal && dealsForCountry.every(({ empty }) => empty)) return

          return {
            country: key,
            deals: dealsForCountry,
          }
        })
        .filter(Boolean)
        .sort((a, b) => a.country.localeCompare(b.country))
    },
    hasDeals() {
      return this.mappedCountriesToDeals.some(({ deals }) => deals.length)
    },
    headers() {
      return [{ text: '', value: '' }].concat(
        this.filteredDeals.map(({ partner, type, name, offer_types }) => ({
          text: `${this.getPartnerNameForSlug(partner)} (${this.formattedOption('dealTypes', type)})`,
          value: partner,
          offerTypes: offer_types.map(t => this.formattedOption('offerTypes', t)),
          name,
        }))
      )
    },
    hideNoDealChecked() {
      return this.query.params.hideNoDeal
    },
    filteredCountries() {
      return this.query.params.countries
    },
  },
  watch: {
    hideNoDealChecked(checked) {
      if (checked) this.query.params.countries = []
    },
    filteredCountries(countries) {
      if (!countries || !countries.length) return
      this.query.params.hideNoDeal = false
    },
  },
  async created() {
    await Promise.all([this.getDeals(), this.getPartners(), this.getTrafficRules()])
    this.setPageLoading(false)
  },
  mounted() {
    /**
     * Table does not render fast enough on mounted or nextTick so we
     * need to setInterval to check when it has rendered to add a listener.
     */
    this.tableRenderCheckInterval = setInterval(() => {
      if (!this.tableLoaded) {
        this.tableLoaded = this.$refs['table-body'] && this.$refs['table-body'].$el
        if (this.tableLoaded) {
          this.addTableScrollListener()
          clearInterval(this.tableRenderCheckInterval)
        }
      }
    }, 500)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    /**
     * Force the table-head to scroll horizontally with the table-body
     * as they are not attached to the same parent.
     */
    addTableScrollListener() {
      this.$refs['table-body'].$el.addEventListener('scroll', () => {
        this.$refs['table-head'].scrollLeft = this.$refs['table-body'].$el.scrollLeft
      })
    },
    resetFilters() {
      this.$set(this.query, 'params', resetQuery())
    },
    async getDeals() {
      this.isLoading = true
      try {
        this.deals = await deals.getActiveDeals()
      } catch (error) {
        console.log(error)
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve deals',
        })
      }

      this.isLoading = false
    },
    async getTrafficRules() {
      this.isLoading = true
      try {
        this.trafficRules = await trafficRules.getTrafficRulesByCountry({ offerType: this.query.params.offerType })
      } catch (error) {
        console.log(error)
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve traffic rules',
        })
      }

      this.isLoading = false
    },
    async getPartners() {
      this.isLoading = true
      try {
        const { data } = await partners.getAllPartners({ compact: true })
        this.partners = data
        this.partnerList = data.map(p => ({ key: p.name, value: p.slug }))
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve partners',
        })
      }

      this.isLoading = false
    },
    getPartnerNameForSlug(slug) {
      const match = this.partners.find(partner => partner.slug === slug)
      if (!match) return slug
      const { name } = match
      return name
    },
    getBestTrafficRule(rule) {
      const type = rule?.type

      switch (type) {
        case 'receive':
          return { type: 'Receive', percentage: rule.percentage, priority: rule.priority }
        case 'block':
          return { type: 'Limit' }
        case 'fullBlock':
          return { type: 'Block' }
        default:
          return undefined
      }
    },
    getCurrencySymbol(currency) {
      switch (currency) {
        case 'EUR':
          return '€'
        case 'GBP':
          return '£'
        default:
          return '$'
      }
    },
    formattedRate(deal) {
      const { currency, grc, rate, type } = deal
      const { value } = rate || {}
      const valueFormatted = type === 'revenue-share' ? `${value}%` : `${currency}${value}`

      return [value && valueFormatted, grc && `(${grc.percentage}%)`].filter(Boolean).join(' ')
    },
    formattedDetails(deal) {
      const { currency, grc, rate, rules, notes, type } = deal
      const { value: rateValue } = rate || {}
      const rateValueFormatted = type === 'revenue-share' ? `${rateValue}%` : `${currency}${rateValue}`

      return [
        rateValue && `${rateValueFormatted} ${rate.qualifier ? `if ${rate.qualifier}` : '- No Qualifier'}`,
        grc && `${grc.percentage}% GRC ${grc.min_lead_volume ? `at ${grc.min_lead_volume} leads` : ''}`,
        rules && `${rules.type} rule in place`,
        rules && rules.percentage && ` - ${rules.percentage}% traffic`,
        rules && rules.priority && ` - Priority ${rules.priority}`,
        notes && `Notes: ${notes}`,
      ].filter(Boolean)
    },
    formatDataAsCsv() {
      const partners = this.headers.map(({ text }, idx) => (idx ? [text, '', '', ''].join(',') : '')).join(',')
      const headings = this.headers
        .map((header, idx) => (idx ? ['Rate', 'Qualifier', 'GRC %', 'Leads'].join(',') : ''))
        .join(',')
      const body = this.mappedCountriesToDeals
        .map(({ country, deals }) => {
          const info = deals.map(({ currency, grc, rate }) => {
            return [
              rate ? `${currency}${rate.value}` : '',
              rate ? rate.qualifier : '',
              grc ? grc.percentage : '',
              grc ? grc.min_lead_volume : '',
            ].join(',')
          })
          return [`"${country}"`].concat(info).join(',')
        })
        .join('\n')

      return [partners, headings, body].join('\n')
    },
    formatDataAsCsvGrc() {
      const partners = this.headers.map(({ text }, idx) => (idx ? text : '')).join(',')
      const headings = this.headers.map((header, idx) => (idx ? 'GRC %' : '')).join(',')
      const body = this.mappedCountriesToDeals
        .map(({ country, deals }) => {
          const info = deals.map(({ grc }) => {
            return grc ? grc.percentage : ''
          })
          return [`"${country}"`].concat(info).join(',')
        })
        .join('\n')

      return [partners, headings, body].join('\n')
    },
    formatDataAsCsvPayout() {
      const partners = this.headers.map(({ text }, idx) => (idx ? text : '')).join(',')
      const headings = this.headers.map((header, idx) => (idx ? 'Payout (rate)' : '')).join(',')
      const body = this.mappedCountriesToDeals
        .map(({ country, deals }) => {
          const info = deals.map(({ currency, rate }) => {
            return rate ? `${currency}${rate.value}` : ''
          })
          return [`"${country}"`].concat(info).join(',')
        })
        .join('\n')

      return [partners, headings, body].join('\n')
    },
    onClickExport() {
      this.isLoading = true

      let data
      switch (this.exportOption) {
        case 'grc':
          data = this.formatDataAsCsvGrc()
          break
        case 'payout':
          data = this.formatDataAsCsvPayout()
          break
        default:
          data = this.formatDataAsCsv()
      }
      const filename = ['deals-matrix', this.query.params.dealType, this.query.params.offerType, this.exportOption]
        .filter(Boolean)
        .join('-')

      exportCSV(data, filename)

      this.isLoading = false
    },
    showDeal(rule) {
      const { trafficRules } = this.query.params
      if (!trafficRules || !trafficRules.length) return false

      if (trafficRules.includes('No Rule') && (!rule || !rule.type)) {
        return true
      } else {
        return !!rule && trafficRules.includes(rule.type)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/settings/_variables';
@import '~vuetify/src/components/VDataTable/_variables';

::v-deep .v-virtual-scroll__item {
  // Needed to force items full-width when horizontal scroll is used.
  right: auto;

  &:last-child {
    .table__cell {
      border-bottom: 0;
    }
  }
}

.table {
  border-radius: $data-table-border-radius;
  line-height: 1.5;
  max-width: 100%;

  &__head {
    display: flex;
    overflow: hidden;
    z-index: 1;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__row {
    position: relative;
    display: flex;

    &:hover .table__cell {
      background-color: map-deep-get($material-light, 'table', 'hover');
    }
  }

  &__cell {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 120px;
    max-width: 120px;
    background-color: #fff;
    padding: 0 16px;
    transition: height $secondary-transition;
    font-size: $data-table-regular-row-font-size;
    height: $data-table-dense-row-height;
    border-right: thin solid map-get($material-light, 'dividers');
    border-bottom: thin solid map-get($material-light, 'dividers');

    &__text {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &--head {
      user-select: none;
      font-size: $data-table-regular-header-font-size;
      font-weight: 700;
      height: $data-table-dense-header-height;
      color: map-deep-get($material-light, 'text', 'secondary');
    }

    &--row-head {
      position: sticky;
      top: 0;
      left: 0;
      z-index: 1;
      min-width: 150px;
      font-weight: 700;
      font-size: $data-table-regular-header-font-size;
    }
  }
}

.has-qualifier {
  position: absolute;
  right: 2px;
  bottom: 2px;
}
</style>
